/**
 * RequireTenant — Shell-level redirect to /onboarding for users with
 * no tenant row.
 *
 * S-060 / FEAT-006 (Phase 3). Wraps every authenticated route except
 * /onboarding itself. On mount it calls `GET /tenants/me`:
 *
 *   - 200 → user has a tenant; render the children.
 *   - 404 → no tenant row for the active Clerk org; redirect to
 *     /onboarding so the user can complete the wizard.
 *   - 401 → JWT was rejected; bubble up so RequireAuth's
 *     <RedirectToSignIn> can pick it up on the next render.
 *   - Other errors → render an inline failure card so the user is
 *     not silently stranded on a blank screen.
 *
 * The check is cached via TanStack Query (key `["tenants", "me"]`,
 * shared with any future component that needs the same row) so we
 * are not refetching on every navigation. The query is invalidated
 * by the OnboardingView after it completes provisioning — Phase 3
 * relies on the post-onboarding hard navigate(/kanban, replace=true)
 * + the page's natural remount to refetch; an explicit
 * `queryClient.invalidateQueries` is not yet wired because there's
 * only one writer of the tenant row in the SPA today.
 *
 * NOTE: Once the user is on /onboarding the wrapper short-circuits
 * — the redirect is suppressed so the wizard does not loop on a
 * still-404 response while the user is in the middle of completing
 * the steps.
 */

import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery, ApiError } from "@/lib/api";
import { tenantGateState } from "@/lib/tenant-status";
import { SuspendedNotice } from "./SuspendedNotice";

// Must match apps/web/src/lib/api.ts JWT_TEMPLATE.
const JWT_TEMPLATE = "ccm-default";

interface TenantRow {
  id: number;
  name: string;
  // FEAT-021 / S-177 — true when the server materialized missing
  // membership rows on this load (invited-user self-heal). The SPA refreshes
  // the JWT so the newly-set active_agencyclient_id claim takes effect.
  healed?: boolean;
  // FEAT-106 / S-425 — active | suspended | purged.
  status?: string;
  purge_after?: string | null;
}

export function RequireTenant({ children }: { children: React.ReactElement }) {
  const { pathname } = useLocation();
  const clerk = useClerk();
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const healRefreshed = useRef(false);
  const isOnboarding = pathname.startsWith("/onboarding");

  // Don't run the check on /onboarding itself — the whole point of the
  // wizard is to fix the missing tenant, and a 404 there would loop.
  const enabled = !isOnboarding;

  const { data, error, isLoading, isFetching } = useApiQuery<TenantRow>(
    ["tenants", "me"],
    "/tenants/me",
    {
      enabled,
      // Retry once on transient 5xx; never retry 401/403/404 — those
      // are deterministic outcomes the UI must react to.
      retry: (failureCount, err) => {
        if (err instanceof ApiError) {
          if (err.status === 401 || err.status === 403 || err.status === 404) {
            return false;
          }
        }
        return failureCount < 1;
      },
      staleTime: 60_000,
      // FEAT-106 / S-425 — while the notice shows, look again every minute,
      // so a reinstated tenant gets the app back without a reload.
      refetchInterval: (query) => (tenantGateState(query.state.data) === "suspended" ? 60_000 : false),
    }
  );

  // FEAT-021 / S-177 — when the server self-healed missing membership rows
  // on this load, the active_agencyclient_id claim was just set in Clerk but
  // the current JWT predates it. Force one fresh mint + broad invalidation so
  // subsequent scoped queries land under the new claim. Guarded by a ref so
  // it runs at most once per mount (the refetched /tenants/me returns
  // healed:false afterwards, so this won't loop regardless).
  useEffect(() => {
    if (data?.healed && !healRefreshed.current) {
      healRefreshed.current = true;
      void (async () => {
        try {
          await getToken({ template: JWT_TEMPLATE, skipCache: true });
        } catch {
          // Non-fatal — the next natural token refresh picks up the claim.
        }
        await qc.invalidateQueries();
      })();
    }
  }, [data?.healed, getToken, qc]);

  // On /onboarding we never gate.
  if (isOnboarding) return children;

  // Still loading — show a minimal placeholder so the shell doesn't
  // flash an empty layout.
  if (enabled && (isLoading || (isFetching && !data && !error))) {
    return (
      <div className="boot-screen">
        <span className="brand-mark">◈</span>
        <span>Loading workspace…</span>
      </div>
    );
  }

  // 404 → no tenant yet → onboarding.
  if (error instanceof ApiError && error.status === 404) {
    return <Navigate to="/onboarding" replace />;
  }

  // 401 → the Clerk JWT was rejected by Hono. Most likely the Clerk
  // session is genuinely stale. Force a sign-out so RequireAuth's
  // <SignedOut><RedirectToSignIn/> fires on the next render — without
  // this nudge, Clerk still thinks we're signed in and the UI loops.
  if (error instanceof ApiError && error.status === 401) {
    return <ForceSignOutAndRedirect clerkSignOut={() => clerk.signOut()} />;
  }

  // Other errors → surface, don't blank the screen.
  if (error) {
    return (
      <div className="boot-screen" style={{ flexDirection: "column", gap: 12 }}>
        <span className="brand-mark">◈</span>
        <strong>Could not load workspace.</strong>
        <span style={{ color: "var(--muted-fg)", fontSize: 13 }}>
          {error instanceof Error ? error.message : "Unknown error"}
        </span>
      </div>
    );
  }

  // FEAT-106 / S-425 — a suspended tenant sees the notice, never the app.
  if (tenantGateState(data) === "suspended") {
    return <SuspendedNotice purgeAfter={data?.purge_after} />;
  }

  return children;
}

function ForceSignOutAndRedirect({
  clerkSignOut,
}: {
  clerkSignOut: () => Promise<void>;
}) {
  useEffect(() => {
    void clerkSignOut();
  }, [clerkSignOut]);
  return (
    <div className="boot-screen">
      <span className="brand-mark">◈</span>
      <span>Session expired — signing you out…</span>
    </div>
  );
}
